import type { ComponentType } from 'react';
import { IonIcon, IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs } from '@ionic/react';
import { Redirect, Route } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export interface TabDef {
  tab: string;
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
}

export interface RouteDef {
  path: string;
  component: ComponentType;
  exact?: boolean;
}

/** Wrapper de lucide-react para IonTabButton (Ionic espera un nodo, no un ícono SVG string). */
function TabIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon className="size-6 mb-0.5" aria-hidden strokeWidth={1.9} />;
}

/**
 * Contenedor con barra de pestañas inferior (móvil y escritorio). Las rutas
 * secundarias se agregan en `routes` y no tienen pestaña propia.
 */
export function TabsShell({ base, tabs, routes, home }: { base: string; tabs: TabDef[]; routes: RouteDef[]; home: string }) {
  return (
    <IonTabs>
      <IonRouterOutlet>
        {routes.map((r) => (
          // IonRouterOutlet no se comporta como <Switch>: sin restringir, '/x/:id' también captura '/x/new'.
          <Route key={r.path} exact={r.exact ?? true} path={r.path.replace(/:id\b/g, ':id([0-9a-fA-F-]{36})')} component={r.component} />
        ))}
        <Route exact path={base}>
          <Redirect to={home} />
        </Route>
      </IonRouterOutlet>
      <IonTabBar slot="bottom" className="pb-[env(safe-area-inset-bottom)]">
        {tabs.map((t) => (
          <IonTabButton key={t.tab} tab={t.tab} href={t.href}>
            <TabIcon icon={t.icon} />
            <IonLabel className="text-[11px] font-medium">{t.label}</IonLabel>
            {!!t.badge && (
              <span className="absolute top-1 right-[calc(50%-18px)] min-w-4 h-4 px-1 rounded-full bg-brand-red text-white text-[10px] leading-4 font-bold">
                {t.badge > 99 ? '99+' : t.badge}
              </span>
            )}
          </IonTabButton>
        ))}
      </IonTabBar>
    </IonTabs>
  );
}

// IonIcon se reexporta para pantallas que necesiten íconos de Ionic.
export { IonIcon };
